import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  Button,
  TextField,
  IconButton,
  Box,
  Typography,
  Grid,
  Paper,
  Stack,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import { Delete, VideoCameraBack } from "@mui/icons-material";
import axios from "axios";
import CloseIcon from "@mui/icons-material/Close";
import VideoRecorder from "./video-recorder";
import DeleteIcon from "@mui/icons-material/Delete";
import { paths } from "../routes/path";
import { useNavigate } from "react-router-dom";
import InteroVideoRecorder from "./intro-video-recorder";
import FarewellVideoRecorder from "./farewell-video-recorder";

const InterviewQuestion = ({
  open,
  onClose,
  position,
  onSave,
  question,
  onSavePosition,
  thinkingTime,
  answerLength,
  questionRetake,
  introVideo,
  createdPositionId,
  isPublicEnabled,
  setCandidatePage,
  farewellVideo,
  jobTitle,
  sharedJobTitle
}) => {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([
    { id: null, question_title: "", full_question: "", video: null },
  ]);
  const [openRecorderIndex, setOpenRecorderIndex] = useState(false);
  const [openIntroRecorder, setOpenIntroRecorder] = useState(false);
  const [openFarewellRecorder, setOpenFarewellRecorder] = useState(false);
  const [videoFile, setVideoFile] = useState(null);
  const [message, setMessage] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [severity, setSeverity] = useState("success");
  const [positionId, setPositionId] = useState(position?.id);
  const [introVideoFile, setIntroVideoFile] = useState(null);
  const [farewellVideoFile, setFarewellVideoFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const [title, setTitle] = useState("Done");
  const [description, setDescription] = useState(
    "Thank you for completing the interview, we will contact you shortly."
  );

  const handleSaveIntroVideo = (blob) => {
    setIntroVideoFile(blob);
  };
  const handleSaveFarewellVideo = (blob) => {
    setFarewellVideoFile(blob);
  };
  const showSnackbar = (msg, type) => {
    setMessage(msg);
    setSeverity(type);
    setOpenSnackbar(true);
  };

  const addQuestion = () => {
    setQuestions([
      ...questions,
      {
        id: null, // Always set to null for new questions
        question_title: "",
        full_question: "",
        video: null,
      },
    ]);
  };

  useEffect(() => {
    if (question && question.length > 0) {
      const formattedQuestions = question.map((q) => ({
        id: q.id, // Keep the original ID from API
        question_title: q.question_title || "",
        full_question: q.full_question || "",
        video: q.video || null,
      }));
      setQuestions(formattedQuestions);
    }
  }, [question]);

  const removeQuestion = (id, index) => {
    // For questions with actual IDs (existing questions from API)
    if (id !== null) {
      fetch(`https://api.a2ajobs.com/api/questions/${id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error("Failed to delete question");
          }
          setQuestions((prev) => prev.filter((q) => q.id !== id));
          showSnackbar(`Question deleted successfully!`, "success");
        })
        .catch((error) => {
          console.error("Error deleting question:", error);
          showSnackbar("Failed to delete question!", "error");
        });
    } else {
      // For new questions (id: null), remove by index
      setQuestions((prev) => prev.filter((_, i) => i !== index));
    }
  };

  const handleInputChange = (index, field, value) => {
    setQuestions((prevQuestions) =>
      prevQuestions.map((q, i) => (i === index ? { ...q, [field]: value } : q))
    );
  };

  const POSITION_API_URL =
    "https://api.a2ajobs.com/api/position-create";
  const QUESTION_API_URL =
    "https://api.a2ajobs.com/api/question-create";

  const handleSaveQuestions = async () => {
    if (questions.length === 0) {
      alert("No questions to save!");
      return;
    }
    setLoading(true);
    try {

      let updatedPositionId = positionId || createdPositionId;

      if (!updatedPositionId && question?.length > 0) {
        const firstQuestion = question[0];
        if (firstQuestion && firstQuestion.position_id) {
          updatedPositionId = firstQuestion.position_id;
        }
      }

      if (!updatedPositionId) {
        const positionResponse = await axios.post(POSITION_API_URL, {
          title: jobTitle || "Write The Job Title Here",
          public_link_enabled: isPublicEnabled ? 1 : 0,
          candidate_thinking_time: thinkingTime,
          candidate_max_answer_length: answerLength,
          question_retakes: questionRetake,
        });

        updatedPositionId = positionResponse.data.data.position_id;
        setPositionId(updatedPositionId);
        navigate(paths.addPosition, {
          state: { addPositionId: updatedPositionId || position?.id || position },
        });
      } else {
      }

      onSavePosition(updatedPositionId);

      const formData = new FormData();
      formData.append("position_id", updatedPositionId);

      // Add questions with proper ID handling
      questions.forEach((question, index) => {
        // Only include ID for existing questions (not null)
        if (question.id !== null) {
          formData.append(`questions[${index}][id]`, question.id);
        }

        formData.append(
          `questions[${index}][question_title]`,
          question.question_title || "Add Title Here"
        );
        formData.append(
          `questions[${index}][full_question]`,
          question.full_question || "Add more details here"
        );
        formData.append(`questions[${index}][position]`, index + 1);

        if (videoFile?.[index]) {
          formData.append(
            `questions[${index}][video]`,
            videoFile[index],
            `video_${index}.mp4`
          );
        }
      });

      formData.append("close_title", title);
      formData.append("close_description", description);

      if (introVideoFile) {
        const introVideoFileConverted =
          introVideoFile instanceof File
            ? introVideoFile
            : new File([introVideoFile], "intro_video.mp4", {
              type: "video/mp4",
            });
        formData.append("intro_video", introVideoFileConverted);
      }
      if (farewellVideoFile) {
        const farewellVideoFileConverted =
          farewellVideoFile instanceof File
            ? farewellVideoFile
            : new File([farewellVideoFile], "close_video.mp4", {
              type: "video/mp4",
            });
        formData.append("close_video", farewellVideoFileConverted);
      }
      const response = await axios.post(QUESTION_API_URL, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      try {
        const positionResponse = await axios.get(
          `https://api.a2ajobs.com/api/get-position/${updatedPositionId}`
        );
        const questionsResponse = await axios.get(
          `https://api.a2ajobs.com/api/questions?position_id=${updatedPositionId}`
        );

        onSave({
          position: positionResponse.data.data,
          questions: questionsResponse.data.data || []
        });
      } catch (fetchError) {
        console.error("Error fetching updated data:", fetchError);
        onSave(questions.length);
      }
      window.location.reload(true);

    } catch (error) {
      console.error(
        "❌ Error saving questions:",
        error.response ? error.response.data : error.message
      );
      onSave(questions.length);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveVideo = (index, blob) => {
    setVideoFile((prev) => ({
      ...prev,
      [index]: blob,
    }));
  };

  useEffect(() => {
    if (question?.length) {
      setQuestions(question);
    }
  }, [question]);

  const handleDeleteVideo = (e) => {
    e.stopPropagation();
    setVideoFile(null);
  };
  const handleDeleteIntroVideo = (e) => {
    e.stopPropagation();
    setIntroVideoFile(null);
  };
  const handleDeletefarewellVideo = (e) => {
    e.stopPropagation();
    setFarewellVideoFile(null);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullScreen>
      <Box sx={{ p: { md: 3, xs: 1 }, mx: { md: 15, xs: 0 } }}>
        <DialogContent>
          <Box sx={{ p: { md: 2, xs: 0 } }}>
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <IconButton onClick={onClose}>
                <CloseIcon />
              </IconButton>
            </Box>
            <Paper sx={{ p: { md: 2, xs: 1 }, mb: 2, textAlign: "start" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                {/* Video Upload Box */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "2px dashed #ccc",
                    padding: 3,
                    borderRadius: 2,
                    height: 120,
                    width: { md: "25%", xs: "80%" },
                    textAlign: "center",
                    bgcolor: "#F8F9FA",
                    transition: "all 0.3s ease-in-out",
                    "&:hover": {
                      border: "2px dashed blue",
                      bgcolor: "#E3F2FD",
                      cursor: "pointer",
                      "& .icon, & .text": {
                        color: "blue",
                      },
                    },
                  }}
                  onClick={() => setOpenIntroRecorder(true)}
                >
                  {introVideo || introVideoFile ? (
                    <Box
                      sx={{ position: "relative", width: "100%", height: "100%" }}
                    >
                      <video
                        src={
                          introVideo
                            ? introVideo
                            : URL.createObjectURL(introVideoFile)
                        }
                        style={{ width: "100%", height: "100%" }}
                        controls
                      />
                      {introVideoFile && (
                        <IconButton
                          onClick={handleDeleteIntroVideo}
                          sx={{
                            position: "absolute",
                            top: 5,
                            right: 5,
                          }}
                        >
                          <DeleteIcon sx={{ color: "white" }} />
                        </IconButton>
                      )}
                    </Box>
                  ) : (
                    <>
                      <IconButton className="icon">
                        <VideoCameraBack sx={{ fontSize: 40, color: "gray" }} />
                      </IconButton>
                      <Typography className="text" fontSize={16} fontWeight={500}>
                        Add Intro Video
                      </Typography>
                      <Typography
                        className="text"
                        fontSize={14}
                        color="text.secondary"
                      >
                        Or drop video file here
                      </Typography>
                    </>
                  )}
                </Box>

                {/* Job Title Text */}
                <Typography variant="h6" sx={{ fontWeight: "bold", flex: 1 }}>
                  {sharedJobTitle}
                </Typography>
              </Box>
            </Paper>
            {questions.map((question, index) => {
              return (
                <Paper key={question.id || `new-${index}`} sx={{ p: 2, mb: 2 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid
                      item
                      xs={12}
                      sm={1}
                      md={1}
                      textAlign={{ xs: "center", sm: "left" }}
                    >
                      <Typography variant="h6">{index + 1}</Typography>
                    </Grid>

                    <Grid
                      item
                      xs={12}
                      sm={3}
                      md={3}
                      display="flex"
                      justifyContent={{ xs: "center", sm: "flex-start" }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          border: "2px dashed #ccc",
                          padding: 3,
                          borderRadius: 2,
                          height: 120,
                          width: { xs: "90%", sm: 170 },
                          textAlign: "center",
                          bgcolor: "#F8F9FA",
                          "&:hover": {
                            border: "2px dashed blue",
                            bgcolor: "#E3F2FD",
                            cursor: "pointer",
                            "& .icon, & .text": {
                              color: "blue",
                            },
                          },
                        }}
                        onClick={() => setOpenRecorderIndex(index)}
                      >
                        {videoFile?.[index] || question?.videoUrl ? (
                          <Box
                            sx={{
                              position: "relative",
                              width: "100%",
                              height: "100%",
                            }}
                          >
                            <video
                              src={
                                videoFile?.[index]
                                  ? URL.createObjectURL(videoFile[index])
                                  : question?.videoUrl
                              }
                              style={{ width: "100%", height: "100%" }}
                              controls
                            />
                            {videoFile?.[index] && (
                              <IconButton
                                onClick={handleDeleteVideo}
                                sx={{
                                  position: "absolute",
                                  top: 5,
                                  right: 5,
                                  bgcolor: "rgba(0,0,0,0.5)",
                                  "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
                                }}
                              >
                                <DeleteIcon sx={{ color: "white" }} />
                              </IconButton>
                            )}
                          </Box>
                        ) : (
                          <>
                            <IconButton className="icon">
                              <VideoCameraBack
                                sx={{ fontSize: 40, color: "gray" }}
                              />
                            </IconButton>
                            <Typography
                              className="text"
                              fontSize={16}
                              fontWeight={500}
                            >
                              Add Video
                            </Typography>
                            <Typography
                              className="text"
                              fontSize={14}
                              color="text.secondary"
                            >
                              Or drop video file here
                            </Typography>
                          </>
                        )}
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={6}>
                      <TextField
                        fullWidth
                        label="Question Title (Max 40 chars)"
                        variant="outlined"
                        size="small"
                        required={true}
                        sx={{ mb: 1 }}
                        value={question?.question_title}
                        onChange={(e) =>
                          handleInputChange(
                            index,  // Changed from question.id to index
                            "question_title",
                            e.target.value
                          )
                        }
                      />

                      <TextField
                        fullWidth
                        label="Full Question (Max 1200 chars)"
                        variant="outlined"
                        size="small"
                        required={true}
                        multiline
                        rows={2}
                        value={question?.full_question}
                        onChange={(e) =>
                          handleInputChange(
                            index,  // Changed from question.id to index
                            "full_question",
                            e.target.value
                          )
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={2} md={2} textAlign="center">
                      <Stack
                        direction="row"
                        justifyContent="center"
                        spacing={1}
                      >
                        <IconButton onClick={() => removeQuestion(question.id, index)}>
                          <Delete />
                        </IconButton>
                      </Stack>
                    </Grid>
                  </Grid>
                  {openRecorderIndex === index && (
                    <VideoRecorder
                      onClose={() => setOpenRecorderIndex(false)}
                      onSave={(blob) => {
                        handleSaveVideo(index, blob);
                      }}
                    />
                  )}
                </Paper>
              );
            })}

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                border: "2px dashed #ccc",
                padding: 3,
                borderRadius: 2,
                height: 20,
                width: "auto",
                textAlign: "center",
                bgcolor: "#F8F9FA",
                my: 2,
                "&:hover": {
                  border: "2px dashed blue",
                },
              }}
              onClick={addQuestion}
            >
              <Typography
                fontSize={16}
                fontWeight={500}
                sx={{
                  "&:hover": {
                    color: "blue",
                  },
                }}
              >
                + Add Question
              </Typography>
            </Box>

            <Paper sx={{ p: 2 }}>
              <Grid container alignItems="center">
                <Grid item xs={12} md={5} textAlign="center">
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "2px dashed #ccc",
                      padding: 3,
                      borderRadius: 2,
                      height: 120,
                      width: { md: "60%", xs: "100%" },
                      textAlign: "center",
                      bgcolor: "#F8F9FA",
                      "&:hover": { border: "2px dashed blue" },
                    }}
                    onClick={() => setOpenFarewellRecorder(true)}
                  >
                    {farewellVideo || farewellVideoFile ? (
                      <Box
                        sx={{
                          position: "relative",
                          width: "100%",
                          height: "100%",
                        }}
                      >
                        <video
                          src={
                            farewellVideo
                              ? farewellVideo
                              : URL.createObjectURL(farewellVideoFile)
                          }
                          style={{ width: "100%", height: "100%" }}
                          controls
                        />
                        {farewellVideoFile && (
                          <IconButton
                            onClick={handleDeletefarewellVideo}
                            sx={{
                              position: "absolute",
                              top: 5,
                              right: 5,
                            }}
                          >
                            <DeleteIcon sx={{ color: "white" }} />
                          </IconButton>
                        )}
                      </Box>
                    ) : (
                      <>
                        <IconButton className="icon">
                          <VideoCameraBack
                            sx={{ fontSize: 40, color: "gray" }}
                          />
                        </IconButton>
                        <Typography fontSize={16} fontWeight={500}>
                          Add Farewell Video
                        </Typography>
                        <Typography fontSize={14} color="text.secondary">
                          Or drop video file here
                        </Typography>
                      </>
                    )}
                  </Box>
                </Grid>

                <Grid item xs={12} md={7}>
                  <TextField
                    fullWidth
                    label="Video Title"
                    variant="outlined"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label="Description"
                    variant="outlined"
                    multiline
                    rows={3}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Box>
        </DialogContent>
        <Box
          display="flex"
          justifyContent="space-between"
          my={3}
          mx={{ md: 5, xs: 1 }}
          gap={{ md: 0, xs: 2 }}
        >
          <Button
            onClick={handleSaveQuestions}
            variant="contained"
            color="primary"
            disabled={loading}
            sx={{ bgcolor: "#1666EB" }}
            startIcon={
              loading && <CircularProgress size={20} color="inherit" />
            }
          >
            {loading ? "Saving..." : "Save & Go Back"}
          </Button>

          {positionId && (
            <Button
              variant="contained"
              sx={{
                background: "#e0e0e0",
                color: "#404040",
                textTransform: "none",
                borderRadius: "6px",
              }}
              onClick={() => setCandidatePage(true)}
            >
              Invite Candidates
            </Button>
          )}
        </Box>
      </Box>
      {openIntroRecorder && (
        <InteroVideoRecorder
          onSave={handleSaveIntroVideo}
          onClose={() => setOpenIntroRecorder(false)}
        />
      )}
      {openFarewellRecorder && (
        <FarewellVideoRecorder
          onSave={handleSaveFarewellVideo}
          onClose={() => setOpenFarewellRecorder(false)}
        />
      )}

      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity={severity}
          variant="filled"
        >
          {message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default InterviewQuestion;